import React, {useCallback, useRef} from "react";
import {useTranslation} from "react-i18next";
//local
import {Tag} from "components/Tag";
import {CustomTooltip} from "components/CustomTooltip";
import {InitialsBadge} from "components/InitialsBadge";
import {CustomCounter} from "components/CustomCounter";
import {getFullName} from "helpers/helper";
import {IconPlus, IconHandOnRight} from "components/icons";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {color} from "constants/color.consts";
import cn from "classnames";

export const ModalRecipientMembersList = ({
  members,
  organization,
  currentUserId,
  handleAddMember,
}) => {
  const {t} = useTranslation();
  const scrollRef = useRef(null);

  const saveScrollPosition = () => (scrollRef.current ? scrollRef.current.scrollTop : 0);
  const restoreScrollPosition = (position) =>
    scrollRef.current && Object.assign(scrollRef.current, {scrollTop: position});

  const addMember = (id) => {
    const scrollPosition = saveScrollPosition();
    handleAddMember({id});
    setTimeout(() => restoreScrollPosition(scrollPosition), 0);
  };

  const initialsTooltip = useCallback(
    (members, firstName) =>
      t("RECIPIENT_MEMBERS_MODAL_ITEM_CONTACTS_TOOLTIP")?.format(
        members.map(({User}) => getFullName(User?.firstName, User?.lastName)).join(", "),
        firstName
      ),
    [t]
  );

  const regionsTooltip = useCallback(
    (firstName, organizationName, regions) =>
      t("RECIPIENT_MEMBERS_MODAL_ITEM_REGIONS_TOOLTIP")?.format(
        firstName,
        organizationName,
        regions.map(t).join(", ")
      ),
    [t]
  );

  const handledRegionsTooltip = useCallback(
    (firstName, regions) =>
      t("RECIPIENT_MEMBERS_MODAL_ITEM_HANDLED_REGIONS_TOOLTIP")?.format(
        firstName,
        regions.map(t).join(", ")
      ),
    [t]
  );

  return (
    <div className="recipients-tab__members-modal__content__list" ref={scrollRef}>
      {members.length ? (
        members.map((item) => (
          <div
            key={item.id}
            className={cn("recipients-tab__members-modal__content__list__row", {
              __selected: item.isSelected,
            })}
          >
            <div className="recipients-tab__members-modal__content__list__item">
              {item.isSelected && <IconHandOnRight addClass="mr-2" color={color.green} />}
              <span>{getFullName(item.firstName, item.lastName)}</span>
              {item.email && <span className="ml-2 mid-gray">{item.email}</span>}
            </div>
            <div className="df-row-center fs0">
              {!!item.OrganizationMember.availableIn?.length && (
                <CustomTooltip
                  text={regionsTooltip(
                    item.firstName,
                    organization.name,
                    item.OrganizationMember.availableIn
                  )}
                  placement="top"
                >
                  <span className="df-row-center">
                    <Tag
                      text={t(item.OrganizationMember.availableIn[0])}
                      color={color.green}
                      bold
                      miniSelectView
                    />
                    {item.OrganizationMember.availableIn?.length > 1 && (
                      <CustomCounter
                        count={item.OrganizationMember.availableIn.length - 1}
                        tiny
                      />
                    )}
                  </span>
                </CustomTooltip>
              )}

              {!!item.OurOrganizationMembers.length && (
                <CustomTooltip
                  text={initialsTooltip(item.OurOrganizationMembers, item.firstName)}
                  placement="bottom"
                >
                  <span className="df-row-center">
                    <InitialsBadge
                      firstName={item.OurOrganizationMembers[0].User.firstName}
                      lastName={item.OurOrganizationMembers[0].User.lastName}
                      isCurrentUser={
                        item.OurOrganizationMembers[0].UserId === currentUserId
                      }
                      counter={item.OurOrganizationMembers.length - 1}
                      addClass="ml-2"
                    />
                  </span>
                </CustomTooltip>
              )}

              {!!item.OurAllHandledAvailableIn?.length && (
                <CustomTooltip
                  text={handledRegionsTooltip(
                    item.firstName,
                    item.OurAllHandledAvailableIn
                  )}
                  placement="top"
                >
                  <span className="df-row-center">
                    <Tag
                      text={t(item.OurAllHandledAvailableIn[0])}
                      color={color.green}
                      bold
                      noBackground
                      miniSelectView
                      addClass="ml-1"
                    />
                    {item.OurAllHandledAvailableIn?.length > 1 && (
                      <CustomCounter
                        count={item.OurAllHandledAvailableIn?.length - 1}
                        tiny
                      />
                    )}
                  </span>
                </CustomTooltip>
              )}

              {!item.isSelected && (
                <ButtonTransparent
                  small
                  icon={<IconPlus />}
                  name="ADD"
                  addClass="recipients-tab__members-modal__content__list__item__action-button"
                  onClick={() => addMember(item.id)}
                />
              )}
            </div>
          </div>
        ))
      ) : (
        <div className="mt-2 __text-center __italic">
          {t("RECIPIENT_MEMBERS_MODAL_LIST_NO_RESULTS")}
        </div>
      )}
    </div>
  );
};
